/**
 * Custom hooks for ViewAllNotificationsPage
 */

import { useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Notification,
  notificationApiRef,
} from '../../api/notificationApi';
import { extractHashtags } from '../../utils/labelParser';
import type { TabKey, TimeFilterType } from './types';

type NotificationSignal = {
  action?: string;
  notification_ids?: string[];
};

function getTimeCutoff(activeTime: TimeFilterType): Date | null {
  const now = new Date();
  switch (activeTime) {
    case 'past-day':
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case 'past-week':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case 'past-month':
      return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    case 'past-year':
      return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    default:
      return null;
  }
}

/** Hook for managing notifications data, filtering, and pagination */
export function useNotifications() {
  const notificationsApi = useApi(notificationApiRef);
  const { lastSignal } = useSignal('notifications');

  const [tab, setTab] = useState<TabKey>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Raw items fetched from API (either one page or all, depending on mode)
  const [rawItems, setRawItems] = useState<Notification[]>([]);
  // Server-reported total (used only in server-pagination mode)
  const [serverTotalCount, setServerTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  // Filter state — all owned by the hook so it can decide fetch strategy
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTime, setActiveTime] = useState<TimeFilterType>('all');

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // Ref to always hold the latest loadPage without adding it to every effect's deps
  const loadPageRef = useRef<(pageNumber: number) => Promise<void>>(
    async () => {},
  );
  const isInitialMount = useRef(true);

  // Debounce search input so we don't fire an API call on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Map tab to the read filter expected by the backend
  const tabReadFilter: boolean | undefined = (() => {
    if (tab === 'unread') return false;
    if (tab === 'read') return true;
    return undefined;
  })();

  // Full client-side pass needed only when filters have no backend equivalent:
  // label filtering (hashtags in description), time-range filtering,
  // and multi-category OR filtering (backend only accepts a single topic).
  const needsFullDataset =
    selectedLabels.length > 0 ||
    activeTime !== 'all' ||
    selectedCategories.length > 1;

  /** All unique hashtag labels across ALL loaded items (not just current page) */
  const availableLabels = useMemo(() => {
    const allLabels = new Set<string>();
    rawItems.forEach(item => {
      const description =
        item.payload.description ??
        item.payload.info ??
        item.payload.message ??
        '';
      extractHashtags(description).forEach(tag => allLabels.add(tag));
    });
    return Array.from(allLabels).sort((a, b) => a.localeCompare(b));
  }, [rawItems]);

  /**
   * Apply client-side filters to rawItems.
   * - Single category: handled server-side via topic param.
   * - Multiple categories: OR-filtered here (backend accepts only one topic).
   * - Labels, time: always client-side.
   * In server mode (needsFullDataset=false), rawItems is already the correct page.
   */
  const filteredItems = useMemo(() => {
    let result = rawItems;

    if (selectedCategories.length > 1) {
      result = result.filter(item =>
        selectedCategories.includes(item.payload.topic as any),
      );
    }

    if (selectedLabels.length > 0) {
      result = result.filter(item => {
        const description =
          item.payload.description ??
          item.payload.info ??
          item.payload.message ??
          '';
        const itemLabels = extractHashtags(description);
        return selectedLabels.every(label => itemLabels.includes(label));
      });
    }

    if (activeTime !== 'all') {
      const cutoff = getTimeCutoff(activeTime);
      if (cutoff) {
        result = result.filter(
          n => !n.created || new Date(n.created) >= cutoff,
        );
      }
    }

    return result;
  }, [rawItems, selectedCategories, selectedLabels, activeTime]);

  // In client mode: paginate filteredItems locally.
  // In server mode: rawItems is already the correct page (filteredItems = rawItems).
  const displayItems = useMemo(() => {
    if (!needsFullDataset) return filteredItems;
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [needsFullDataset, filteredItems, page, pageSize]);

  const totalCount = needsFullDataset ? filteredItems.length : serverTotalCount;
  const activeFilterCount = selectedCategories.length + selectedLabels.length;

  const refreshStatus = useCallback(async () => {
    try {
      const status = await notificationsApi.getStatus();
      setUnreadCount(status.unread ?? 0);
    } catch {
      /* Non-critical: keep previous count on transient failures */
    }
  }, [notificationsApi]);

  /**
   * Maximum number of items fetched when client-side label/time filtering is active.
   * Fetching unbounded sets risks performance issues for users with many notifications.
   */
  const MAX_CLIENT_FILTER_ITEMS = 500;

  /**
   * Load notifications.
   * - needsFullDataset (label/time filters active): fetch up to MAX_CLIENT_FILTER_ITEMS, filter locally.
   *   Search is still passed to backend to pre-narrow the dataset.
   * - Server mode: fetch one page from backend with all supported filters delegated.
   */
  const loadPage = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      setError(null);
      try {
        const topic =
          selectedCategories.length === 1 ? selectedCategories[0] : undefined;
        // When multiple categories are selected, topic is omitted and
        // client-side OR filtering is applied in filteredItems.

        if (needsFullDataset) {
          // Fetch up to MAX_CLIENT_FILTER_ITEMS — label/time filters need the full (pre-searched) dataset
          const response = await notificationsApi.getNotifications({
            limit: MAX_CLIENT_FILTER_ITEMS,
            offset: 0,
            read: tabReadFilter,
            saved: false,
            search: debouncedSearch || undefined,
            topic,
          });
          const all = response.notifications ?? [];
          setRawItems(all);
        } else {
          const offset = Math.max(0, (pageNumber - 1) * pageSize);
          const response = await notificationsApi.getNotifications({
            limit: pageSize,
            offset,
            read: tabReadFilter,
            saved: false,
            search: debouncedSearch || undefined,
            topic,
          });
          const notifications = response.notifications ?? [];
          setServerTotalCount(response.totalCount ?? 0);
          setRawItems(notifications);
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to load notifications',
        );
      } finally {
        setLoading(false);
      }
    },
    [
      notificationsApi,
      pageSize,
      selectedCategories,
      needsFullDataset,
      tabReadFilter,
      debouncedSearch,
    ],
  );

  const markAllAsRead = useCallback(async () => {
    setBulkActionLoading(true);
    setError(null);
    try {
      const ids: string[] = [];
      const fetchLimit = 100;
      const maxPages = 50; // cap at 5 000 notifications to avoid runaway loops
      let offset = 0;
      const topic =
        selectedCategories.length === 1 ? selectedCategories[0] : undefined;
      for (let pageIdx = 0; pageIdx < maxPages; pageIdx++) {
        const response = await notificationsApi.getNotifications({
          read: false,
          limit: fetchLimit,
          offset,
          topic,
          search: debouncedSearch || undefined,
        });
        const pageItems = response.notifications ?? [];
        ids.push(...pageItems.map(n => n.id));
        if (pageItems.length < fetchLimit) break;
        offset += fetchLimit;
      }
      if (ids.length === 0) {
        setBulkActionLoading(false);
        return;
      }
      const chunkSize = 100;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        if (chunk.length)
          await notificationsApi.updateNotifications({
            ids: chunk,
            read: true,
          });
      }
      await refreshStatus();
      setPage(1);
      await loadPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark all as read');
    } finally {
      setBulkActionLoading(false);
    }
  }, [
    loadPage,
    notificationsApi,
    refreshStatus,
    selectedCategories,
    debouncedSearch,
  ]);

  const toggleNotificationRead = useCallback(
    async (notificationId: string, currentReadStatus: boolean) => {
      const newRead = !currentReadStatus;
      // Optimistic update
      setRawItems(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: newRead } : n)),
      );
      try {
        await notificationsApi.updateNotifications({
          ids: [notificationId],
          read: newRead,
        });
        await refreshStatus();
      } catch (e) {
        // Revert the optimistic update so the UI reflects actual state
        setRawItems(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, read: currentReadStatus } : n,
          ),
        );
        setError(
          e instanceof Error ? e.message : 'Failed to update notification',
        );
      }
    },
    [notificationsApi, refreshStatus],
  );

  const markAsRead = useCallback(
    async (notificationId: string) => {
      // Optimistically mark as read so blue styling clears immediately
      setRawItems(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n)),
      );
      try {
        await notificationsApi.updateNotifications({
          ids: [notificationId],
          read: true,
        });
        await refreshStatus();
      } catch (e) {
        // Revert optimistic update on failure
        setRawItems(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, read: false } : n)),
        );
        setError(
          e instanceof Error
            ? e.message
            : 'Failed to mark notification as read',
        );
      }
    },
    [notificationsApi, refreshStatus],
  );

  const deleteNotification = useCallback(
    async (notificationId: string) => {
      setRawItems(prev => prev.filter(n => n.id !== notificationId));
      try {
        await notificationsApi.updateNotifications({
          ids: [notificationId],
          saved: true,
        });
        await refreshStatus();
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to remove notification',
        );
        await loadPage(page);
      }
    },
    [notificationsApi, refreshStatus, loadPage, page],
  );

  const bulkUpdate = useCallback(
    async (ids: string[], options: { read?: boolean; saved?: boolean }) => {
      if (ids.length === 0) return;
      try {
        await notificationsApi.updateNotifications({ ids, ...options });
        await refreshStatus();
        await loadPage(page);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Bulk action failed');
        await loadPage(page);
      }
    },
    [notificationsApi, refreshStatus, loadPage, page],
  );

  // Keep ref in sync with latest loadPage so effects don't need it as a dep
  useEffect(() => {
    loadPageRef.current = loadPage;
  }, [loadPage]);

  // Initial load — fires exactly once on mount
  useEffect(() => {
    loadPageRef.current(1);
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when tab or categories change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    setPage(1);
    loadPageRef.current(1);
    refreshStatus();
  }, [tab, selectedCategories, refreshStatus]);

  // Reload when search or pageSize changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    setPage(1);
    loadPageRef.current(1);
  }, [debouncedSearch, pageSize]);

  // Reload when needsFullDataset toggles OR when activeTime changes.
  // Also marks end of initial mount phase.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
    loadPageRef.current(1);
  }, [needsFullDataset, activeTime]);

  // Debounce ref: coalesces burst signals (e.g. bulk mark-read) into one reload.
  const signalDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Real-time updates via WebSocket (useSignal).
  // Known action types are handled optimistically with local state updates to
  // avoid unnecessary API calls. Unknown actions fall back to a debounced reload.
  useEffect(() => {
    if (!lastSignal?.action) return;
    const action = lastSignal.action;

    const signal = lastSignal as NotificationSignal;

    if (action === 'notification_read' || action === 'notification_unread') {
      const ids = signal.notification_ids ?? [];
      const isRead = action === 'notification_read';
      setRawItems(prev =>
        prev.map(n => (ids.includes(n.id) ? { ...n, read: isRead } : n)),
      );
      refreshStatus();
      return;
    }

    if (action === 'notification_saved') {
      const ids = signal.notification_ids ?? [];
      setRawItems(prev => prev.filter(n => !ids.includes(n.id)));
      refreshStatus();
      return;
    }

    // new_notification and any unknown actions: debounced full reload so a burst
    // of signals (e.g. sending to many recipients) only triggers one API call.
    if (signalDebounceRef.current) clearTimeout(signalDebounceRef.current);
    signalDebounceRef.current = setTimeout(() => {
      refreshStatus();
      loadPageRef.current(page);
    }, 800);
  }, [lastSignal, page, refreshStatus]);

  const pageCount = useMemo(
    () => Math.max(0, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize],
  );

  const showingRange = useMemo(() => {
    if (!totalCount || displayItems.length === 0) return 'Showing 0 of 0';
    const start = (page - 1) * pageSize + 1;
    const end = start + displayItems.length - 1;
    const suffix = needsFullDataset ? ' (filtered)' : '';
    return `Showing ${start}–${end} of ${totalCount}${suffix}`;
  }, [displayItems.length, page, pageSize, totalCount, needsFullDataset]);

  return {
    tab,
    setTab,
    loading,
    error,
    bulkActionLoading,
    items: displayItems,
    totalCount,
    unreadCount,
    showFilters,
    setShowFilters,
    selectedCategories,
    setSelectedCategories,
    selectedLabels,
    setSelectedLabels,
    searchQuery,
    setSearchQuery,
    activeTime,
    setActiveTime,
    availableLabels,
    activeFilterCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    showingRange,
    loadPage,
    markAllAsRead,
    toggleNotificationRead,
    markAsRead,
    deleteNotification,
    bulkUpdate,
  };
}
